import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNoAnswer, PayoutValidateResponse, PayoutInitiateResponse, PayoutConfirmResponse, ActiveClaimSession } from '../lib/api'
import { isWalletAddress, shortAddress, guideUrl, errorKey } from '../lib/claimSteps'

/**
 * The one claim path for a Telegram winner and for a Telegram referrer, step by
 * step (owner, 28.10: a winner did not understand the old single form and could
 * not claim). The money logic is the old CryptoClaimFlow's, unchanged: address →
 * check → $0.10 test → confirm. What changed is that each screen says what it
 * is for, where to get the address, and what happens next — in five languages.
 *
 * Kept from the old flow on purpose: a `pending` payout or an unclear answer
 * never brings the confirm button back (pressing it twice once paid twice), and
 * an `in_flight` answer jumps straight to "on its way".
 *
 * Round #3: a winner closed the app on step 2 and every new try was refused
 * with session_already_active for 30 minutes. On opening, the wizard now asks
 * for the open claim and continues it on step 2 — no second $0.10, no wait.
 * Server refusals are shown in words (errorKey), not as codes.
 */

export interface ClaimApi {
  validate(address: string): Promise<PayoutValidateResponse>
  initiate(address: string): Promise<PayoutInitiateResponse>
  confirm(sessionId: string): Promise<PayoutConfirmResponse>
  cancel(sessionId: string): Promise<unknown>
  /** The person's open claim, prize or reward, if any (GET /api/payout/session). */
  active(): Promise<ActiveClaimSession>
}

interface ClaimWizardProps {
  kind: 'prize' | 'reward'
  /** As shown on the card, e.g. "3.825"; null when not known. */
  amount: string | null
  claim: ClaimApi
  onClose: () => void
}

type Step =
  | 'intro'
  | 'address-form'
  | 'validating'
  | 'warning'
  | 'initiating'
  | 'awaiting-confirmation'
  | 'confirming'
  | 'success'
  | 'sent-pending'
  | 'no-answer'
  | 'error'

const POLYGONSCAN_TX = 'https://polygonscan.com/tx/'

function openExternal(url: string) {
  const tg = window.Telegram?.WebApp
  if (tg && typeof tg.openLink === 'function') tg.openLink(url)
  else window.open(url, '_blank', 'noopener,noreferrer')
}

export function ClaimWizard({ kind, amount, claim, onClose }: ClaimWizardProps) {
  const { t, i18n } = useTranslation()
  const [step, setStep] = useState<Step>('intro')
  const [address, setAddress] = useState('')
  const [pasteFailed, setPasteFailed] = useState(false)
  const [validation, setValidation] = useState<PayoutValidateResponse | null>(null)
  const [session, setSession] = useState<PayoutInitiateResponse | null>(null)
  const [payoutTxHash, setPayoutTxHash] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Set when the open claim belongs to the other card (prize vs reward). */
  const [otherOpen, setOtherOpen] = useState<'prize' | 'reward' | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isPrize = kind === 'prize'
  const title = amount
    ? t(isPrize ? 'claim.prizeTitle' : 'claim.rewardTitle', { amount: `$${amount}` })
    : t(isPrize ? 'claim.prizeTitleNoAmount' : 'claim.rewardTitleNoAmount')
  const addressOk = isWalletAddress(address)

  /** A refusal in the player's words; the server's own text only for an unknown code. */
  const describe = (err: any): string => {
    const key = errorKey(err?.code)
    return key ? t(key) : err?.message || t('claim.errorLead')
  }

  /**
   * Puts the person back into his open claim. Returns true when there was one
   * for this card. A test still on its way is waited for (every 5 s), then step 2.
   */
  const tryResume = async (): Promise<boolean> => {
    let open: ActiveClaimSession
    try {
      open = await claim.active()
    } catch {
      return false // unknown: the normal path still works, and says so if refused
    }
    if (!open.active) return false
    if (open.kind !== kind) {
      setOtherOpen(open.kind)
      return true
    }
    setAddress(open.destinationAddress)
    if (open.step === 'confirm' && open.testTxHash) {
      setSession({ sessionId: open.sessionId, testTxHash: open.testTxHash, expiresAt: open.expiresAt, status: 'awaiting_confirmation' })
      setError(null)
      setStep('awaiting-confirmation')
    } else {
      setStep('initiating')
      pollRef.current = setTimeout(() => void tryResume(), 5000)
    }
    return true
  }

  useEffect(() => {
    void tryResume()
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
    // Once, when the wizard opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePaste = async () => {
    setPasteFailed(false)
    try {
      const text = (await navigator.clipboard.readText()).trim()
      if (text) {
        setAddress(text)
        return
      }
    } catch {
      // Not allowed here (some Telegram clients): fall through to the hint.
    }
    setPasteFailed(true)
  }

  const handleValidate = async () => {
    if (!isWalletAddress(address)) {
      setError(t('claim.addrBad'))
      return
    }
    setError(null)
    setStep('validating')
    try {
      const result = await claim.validate(address)
      if ((result as any).status === 'in_flight' && (result as any).payoutTxHash) {
        setPayoutTxHash((result as any).payoutTxHash)
        setStep('sent-pending')
        return
      }
      setValidation(result)
      if (!result.ok) {
        setError(`${t('claim.rejected')} (${result.reasons.join(', ')})`)
        setStep('address-form')
        return
      }
      if (result.suspicious) {
        setStep('warning')
        return
      }
      await handleInitiate()
    } catch (err: any) {
      setError(describe(err))
      setStep('address-form')
    }
  }

  const handleInitiate = async () => {
    setError(null)
    setStep('initiating')
    try {
      const result = await claim.initiate(address)
      if (result.status === 'in_flight' && result.payoutTxHash) {
        // A payout for you is already on its way; no new test transfer was made.
        setPayoutTxHash(result.payoutTxHash)
        setStep('sent-pending')
        return
      }
      setSession(result)
      setStep('awaiting-confirmation')
    } catch (err: any) {
      if (err?.code === 'session_already_active' && (await tryResume())) return
      setError(describe(err))
      setStep('address-form')
    }
  }

  const handleConfirm = async () => {
    if (!session) return
    setError(null)
    setStep('confirming')
    try {
      const result = await claim.confirm(session.sessionId)
      setPayoutTxHash(result.payoutTxHash)
      // `pending` means signed and sent, not yet seen in a block. It is not a
      // failure, and the confirm button must not come back: pressing it again
      // is exactly how a payout used to go out twice.
      setStep(result.status === 'pending' ? 'sent-pending' : 'success')
    } catch (err: any) {
      // No clear answer: the money may be on its way, so the button must not
      // return. Anything else was refused before signing and may be retried.
      if (isNoAnswer(err)) {
        setStep('no-answer')
        return
      }
      setError(describe(err))
      setStep('awaiting-confirmation')
    }
  }

  /** Leaves the flow, dropping an open test session (best effort). */
  const handleCancel = async () => {
    if (session) {
      try {
        await claim.cancel(session.sessionId)
      } catch {
        // best effort
      }
    }
    onClose()
  }

  /** Back to the address, dropping the open test session: a new address starts a new test. */
  const handleStartAgain = async () => {
    if (session) {
      try {
        await claim.cancel(session.sessionId)
      } catch {
        // best effort
      }
    }
    setSession(null)
    setValidation(null)
    setError(null)
    setStep('address-form')
  }

  const overlay = (children: React.ReactNode) => (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="card w-full max-w-md my-4">{children}</div>
    </div>
  )

  const progress = (n: 1 | 2 | 3) => (
    <div className="mb-4">
      <div className="text-xs text-telegram-hint mb-1">{t('claim.stepOf', { n })}</div>
      <div className="flex gap-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= n ? 'bg-gold-500' : 'bg-telegram-secondary'}`} />
        ))}
      </div>
    </div>
  )

  const errorBox = error && (
    <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4 text-red-400 text-sm break-words">
      {error}
    </div>
  )

  if (otherOpen) {
    return overlay(
      <div className="text-center py-6">
        <div className="text-4xl mb-3">⏳</div>
        <p className="text-telegram-text mb-6">
          {t(otherOpen === 'prize' ? 'claim.otherOpenPrize' : 'claim.otherOpenReward')}
        </p>
        <button onClick={onClose} className="btn-gold w-full">
          {t('claim.close')}
        </button>
      </div>,
    )
  }

  if (step === 'intro') {
    return overlay(
      <div>
        <div className="text-center mb-4">
          <div className="text-4xl mb-2">{isPrize ? '🎉' : '🎁'}</div>
          <h2 className="font-bold text-telegram-text text-xl">{title}</h2>
        </div>
        <p className="text-sm text-telegram-text mb-3">{t('claim.introLead')}</p>
        <ol className="space-y-2 mb-4">
          {[t('claim.introStep1'), t('claim.introStep2'), t(isPrize ? 'claim.introStep3Prize' : 'claim.introStep3Reward')].map(
            (s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-telegram-text">
                <span className="shrink-0 w-6 h-6 rounded-full bg-gold-500 text-black font-bold flex items-center justify-center text-xs">
                  {i + 1}
                </span>
                <span className="pt-0.5">{s}</span>
              </li>
            ),
          )}
        </ol>
        <p className="text-xs text-telegram-hint mb-5">{t('claim.introDeadline')}</p>
        <button onClick={() => setStep('address-form')} className="btn-gold w-full">
          {t('claim.start')}
        </button>
        <button
          onClick={() => openExternal(guideUrl(i18n.language))}
          className="w-full mt-3 py-2 text-gold-500 underline text-sm"
        >
          {t('claim.noWallet')}
        </button>
        <button onClick={onClose} className="w-full mt-1 py-2 text-telegram-hint text-sm">
          {t('claim.close')}
        </button>
      </div>,
    )
  }

  if (step === 'address-form') {
    return overlay(
      <>
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setStep('intro')} className="text-telegram-hint text-sm">
            ← {t('claim.back')}
          </button>
        </div>
        {progress(1)}
        <h2 className="font-bold text-telegram-text text-lg mb-2">{t('claim.addrTitle')}</h2>
        <p className="text-sm text-telegram-text mb-2">{t('claim.addrHow')}</p>
        <p className="text-sm text-gold-500 mb-4">{t('claim.addrNetwork')}</p>
        {errorBox}
        <button onClick={handlePaste} className="btn-gold w-full mb-3">
          {t('claim.paste')}
        </button>
        {pasteFailed && <p className="text-xs text-telegram-hint mb-3">{t('claim.pasteFailed')}</p>}
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={address}
          onChange={(e) => {
            setAddress(e.target.value.trim())
            setError(null)
          }}
          placeholder="0x…"
          className="w-full bg-telegram-secondary rounded-lg px-4 py-3 text-telegram-text font-mono text-sm"
        />
        {address && (
          <p className={`text-xs mt-2 ${addressOk ? 'text-green-400' : 'text-red-400'}`}>
            {addressOk ? `✓ ${t('claim.addrOk', { short: shortAddress(address) })}` : `✗ ${t('claim.addrBad')}`}
          </p>
        )}
        <button onClick={handleValidate} disabled={!addressOk} className="btn-gold w-full mt-5 disabled:opacity-40">
          {t('claim.continue')}
        </button>
      </>,
    )
  }

  if (step === 'validating' || step === 'initiating' || step === 'confirming') {
    const label =
      step === 'validating'
        ? t('claim.checking')
        : step === 'initiating'
          ? t('claim.sendingTest')
          : t(isPrize ? 'claim.sendingPrize' : 'claim.sendingReward')
    return overlay(
      <div>
        {progress(step === 'confirming' ? 3 : step === 'initiating' ? 2 : 1)}
        <div className="text-center py-10">
          <div className="mx-auto mb-4 w-10 h-10 rounded-full border-4 border-gold-500 border-t-transparent animate-spin" />
          <div className="text-telegram-text">{label}</div>
        </div>
      </div>,
    )
  }

  if (step === 'warning' && validation) {
    const reasonText = (r: string) => {
      if (r === 'new_address') return t('claim.warnNew')
      if (r === 'is_contract') return t('claim.warnContract')
      return r
    }
    return overlay(
      <div>
        {progress(1)}
        <div className="text-center mb-4">
          <div className="text-4xl mb-2">⚠️</div>
          <h2 className="font-bold text-telegram-text text-lg">{t('claim.warnTitle')}</h2>
          <p className="text-xs text-telegram-hint font-mono mt-1 break-all">{address}</p>
        </div>
        <ul className="text-sm text-telegram-text space-y-2 mb-4 list-disc pl-5">
          {validation.reasons.map((r, i) => (
            <li key={i}>{reasonText(r)}</li>
          ))}
        </ul>
        <p className="text-xs text-telegram-hint mb-6">{t('claim.warnNote')}</p>
        <button onClick={handleInitiate} className="btn-gold w-full">
          {t('claim.warnProceed')}
        </button>
        <button onClick={handleStartAgain} className="w-full mt-3 py-2 text-telegram-hint text-sm">
          {t('claim.useOther')}
        </button>
      </div>,
    )
  }

  if (step === 'awaiting-confirmation' && session) {
    return overlay(
      <div>
        {progress(2)}
        <div className="text-center mb-4">
          <div className="text-4xl mb-2">✅</div>
          <h2 className="font-bold text-telegram-text text-lg">{t('claim.testTitle')}</h2>
          <p className="text-xs text-telegram-hint font-mono mt-1">{t('claim.testTo', { short: shortAddress(address) })}</p>
        </div>
        <p className="text-sm text-telegram-text mb-2 text-center">{t('claim.testHow')}</p>
        <p className="text-xs text-telegram-hint mb-4 text-center">{t('claim.testWait')}</p>
        <button
          onClick={() => openExternal(POLYGONSCAN_TX + session.testTxHash)}
          className="text-gold-500 underline text-sm block mx-auto mb-5"
        >
          {t('claim.viewTest')}
        </button>
        {errorBox}
        <button onClick={handleConfirm} className="btn-gold w-full">
          {t(isPrize ? 'claim.testYesPrize' : 'claim.testYesReward')}
        </button>
        <button onClick={handleStartAgain} className="w-full mt-3 py-2 text-telegram-hint text-sm">
          {t('claim.startAgain')}
        </button>
        <button onClick={handleCancel} className="w-full mt-1 py-2 text-telegram-hint text-xs">
          {t('claim.close')}
        </button>
      </div>,
    )
  }

  if ((step === 'success' || step === 'sent-pending') && payoutTxHash) {
    return overlay(
      <div>
        {progress(3)}
        <div className="text-center py-4">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="text-xl font-bold text-telegram-text mb-2">
            {t(isPrize ? 'claim.doneTitlePrize' : 'claim.doneTitleReward')}
          </h2>
          {/* No amount here: the card shows the whole prize, and a winner who
              joined through an invite receives 90% of it. */}
          <p className="text-telegram-hint text-sm mb-4">{t('claim.doneBody')}</p>
          <button onClick={() => openExternal(POLYGONSCAN_TX + payoutTxHash)} className="text-gold-500 underline text-sm">
            {t('claim.viewPayout')}
          </button>
          <button onClick={onClose} className="btn-gold w-full mt-6">
            {t('claim.done')}
          </button>
        </div>
      </div>,
    )
  }

  if (step === 'no-answer') {
    return overlay(
      <div className="text-center py-8">
        <h2 className="text-xl font-bold text-telegram-text mb-2">{t('claim.noAnswerTitle')}</h2>
        <p className="text-telegram-hint text-sm mb-4">{t('claim.noAnswerBody')}</p>
        <button onClick={onClose} className="btn-gold w-full mt-6">
          {t('claim.close')}
        </button>
      </div>,
    )
  }

  if (step === 'error') {
    return overlay(
      <div className="text-center py-8">
        <div className="text-telegram-text mb-4">{error || t('claim.errorLead')}</div>
        <button onClick={onClose} className="btn-gold">
          {t('claim.close')}
        </button>
      </div>,
    )
  }

  return null
}
